<template>
  <div>
    <!-- Overlay for mobile -->
    <div
      v-if="isMobileMenuOpen"
      @click="closeMobileMenu"
      class="fixed inset-0 bg-black/50 z-40 lg:hidden"
    ></div>

    <!-- Sidebar -->
    <aside
      :class="[
        'fixed top-0 left-0 h-screen bg-sidebar-bg border-r border-sidebar-border z-50 transition-transform duration-300 flex flex-col custom-scrollbar-dark',
        isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        isCollapsed ? 'w-20' : 'w-64'
      ]"
    >
      <!-- Logo/Brand -->
      <div class="p-4 border-b border-sidebar-border flex items-center justify-between">
        <RouterLink
          to="/"
          class="flex items-center space-x-3 transition-opacity"
          :class="isCollapsed ? 'opacity-0 w-0' : 'opacity-100'"
        >
          <div class="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <span class="text-white font-bold text-lg">CC</span>
          </div>
          <span class="font-semibold text-sidebar-text text-lg">Calmren</span>
        </RouterLink>

        <!-- Collapse button (desktop only) -->
        <button
          @click="toggleCollapse"
          class="hidden lg:block p-2 hover:bg-sidebar-surface rounded transition-colors text-sidebar-text-muted hover:text-sidebar-text"
          :class="isCollapsed ? 'ml-auto' : ''"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              :d="isCollapsed ? 'M13 5l7 7-7 7M5 5l7 7-7 7' : 'M11 19l-7-7 7-7m8 14l-7-7 7-7'"
            />
          </svg>
        </button>
      </div>

      <!-- Dev Mode Banner -->
      <div v-if="isDevelopment" class="bg-warning-500 text-white px-3 py-2 text-xs font-medium text-center">
        🔧 DEV MODE - No Auth
      </div>

      <!-- Navigation -->
      <nav class="flex-1 overflow-y-auto p-4 space-y-1">
        <!-- Public Navigation -->
        <div class="mb-6">
          <p
            v-if="!isCollapsed"
            class="text-xs font-semibold text-sidebar-text-muted uppercase tracking-wider mb-2 px-3"
          >
            Public
          </p>
          <template v-for="item in publicNavigation" :key="item.name">
            <NavItem :item="item" :is-collapsed="isCollapsed" @click="closeMobileMenu" />
          </template>
        </div>

        <!-- Authenticated Navigation -->
        <div v-if="showAuthenticatedItems">
          <p
            v-if="!isCollapsed"
            class="text-xs font-semibold text-sidebar-text-muted uppercase tracking-wider mb-2 px-3"
          >
            Business
          </p>
          <template v-for="item in authenticatedNavigation" :key="item.name">
            <NavItem
              v-if="!item.dropdown"
              :item="item"
              :is-collapsed="isCollapsed"
              @click="closeMobileMenu"
            />

            <!-- Dropdown Item -->
            <div v-else>
              <button
                @click="toggleDropdown(item.name)"
                class="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-sidebar-text hover:bg-sidebar-surface hover:text-white rounded transition-colors"
              >
                <span class="flex items-center space-x-3">
                  <span class="text-xl flex-shrink-0">{{ item.icon }}</span>
                  <span v-if="!isCollapsed">{{ item.name }}</span>
                </span>
                <svg
                  v-if="!isCollapsed"
                  class="w-4 h-4 transition-transform"
                  :class="activeDropdowns.includes(item.name) ? 'rotate-180' : ''"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              <!-- Dropdown Content -->
              <div
                v-show="activeDropdowns.includes(item.name) && !isCollapsed"
                class="ml-6 mt-1 space-y-1"
              >
                <RouterLink
                  v-for="subItem in item.dropdown"
                  :key="subItem.name"
                  :to="subItem.href"
                  @click="closeMobileMenu"
                  class="flex items-center space-x-3 px-3 py-2 text-sm text-sidebar-text-muted hover:text-sidebar-text hover:bg-sidebar-surface rounded transition-colors"
                  :class="{ 'text-primary-400 bg-sidebar-surface': $route.path === subItem.href }"
                >
                  <span>{{ subItem.icon }}</span>
                  <span>{{ subItem.name }}</span>
                </RouterLink>
              </div>
            </div>
          </template>
        </div>
      </nav>

      <!-- Footer: Logout (Production only) -->
      <div v-if="showAuthenticatedItems && isProduction" class="p-4 border-t border-sidebar-border">
        <button
          @click="handleLogout"
          class="w-full flex items-center space-x-3 px-3 py-2 text-sm font-medium text-error-400 hover:text-error-300 hover:bg-sidebar-surface rounded transition-colors"
        >
          <span class="text-xl">🚪</span>
          <span v-if="!isCollapsed">Logout</span>
        </button>
      </div>
    </aside>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { RouterLink } from 'vue-router'
import { authService } from '@/services/auth'
import NavItem from './NavItem.vue'

// State
const isMobileMenuOpen = ref(false)
const isCollapsed = ref(false)
const activeDropdowns = ref<string[]>([])
const isAuthenticated = ref(false)

// Environment detection
const isDevelopment = import.meta.env.DEV
const isProduction = import.meta.env.PROD

// Check auth status on mount
onMounted(async () => {
  const user = await authService.checkAuth()
  isAuthenticated.value = !!user
})

// Public navigation (always visible)
const publicNavigation = [
  { name: 'Home', href: '/', icon: '🏠' },
  { name: 'Contact', href: '/contact', icon: '📧' },
]

// Authenticated navigation (conditional)
const authenticatedNavigation = [
  { name: 'Dashboard', href: '/dashboard', icon: '📊' },
  { name: 'Time Log', href: '/time-log', icon: '⏱️' },
  {
    name: 'Clients',
    icon: '👥',
    dropdown: [
      { name: 'Client Management', href: '/clients', icon: '👥' },
      { name: 'Invoices', href: '/invoices', icon: '📄' },
    ]
  },
  { name: 'Expenses', href: '/expenses', icon: '💳' },
  { name: 'Bank Reconcile', href: '/bank-reconciliation', icon: '🏦' },
  { name: 'Tax Calculator', href: '/tax-calculator', icon: '🧮' },
  { name: 'Business Settings', href: '/business-calculations', icon: '⚙️' },
  { name: 'FSU Projects', href: '/fsu-projects', icon: '🎯' },
]

// Show authenticated items based on environment
const showAuthenticatedItems = computed(() => {
  if (isDevelopment) {
    // Development: Always show (API is open anyway)
    return true
  }
  // Production: Check if user is authenticated
  return isAuthenticated.value
})

// Methods
const toggleCollapse = () => {
  isCollapsed.value = !isCollapsed.value
  // Close all dropdowns when collapsing
  if (isCollapsed.value) {
    activeDropdowns.value = []
  }
}

const toggleDropdown = (name: string) => {
  const index = activeDropdowns.value.indexOf(name)
  if (index > -1) {
    activeDropdowns.value.splice(index, 1)
  } else {
    activeDropdowns.value.push(name)
  }
}

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false
}

const handleLogout = async () => {
  await authService.logout()
}

// Expose method for parent to toggle mobile menu
defineExpose({
  toggleMobileMenu: () => {
    isMobileMenuOpen.value = !isMobileMenuOpen.value
  }
})
</script>
